import type { EventCategory } from '../types';
import type { EventTagId } from '../utils/eventTags';

// Bridges the two vocabularies Tonyte already has: profile interests
// (INTEREST_CATEGORIES in ./interests.ts — 10 broad groups, 100 items chosen
// during onboarding) and event metadata (the 5 EventCategory values plus the
// upload tag ids in utils/eventTags.ts). Nothing here is stored anywhere; it
// only translates a user's existing interests into what an event can match.

export type InterestEventTarget = {
  categories: EventCategory[];
  tags: EventTagId[];
};

export const INTEREST_GROUP_TARGETS: Record<string, InterestEventTarget> = {
  'Travel': { categories: ['adventure', 'culture'], tags: ['activities', 'solo-friendly'] },
  'Food & Drinks': { categories: ['food'], tags: ['food', 'date-idea'] },
  'Nightlife': { categories: ['nightlife'], tags: ['parties', 'high-energy'] },
  'Sports & Fitness': { categories: ['adventure'], tags: ['activities', 'high-energy'] },
  'Music': { categories: ['nightlife', 'culture'], tags: ['parties'] },
  'Art & Creativity': { categories: ['culture'], tags: ['chill'] },
  'Social & Culture': { categories: ['social', 'culture'], tags: ['social', 'solo-friendly', 'student'] },
  'Tech & Gaming': { categories: ['social'], tags: ['social'] },
  'Outdoors': { categories: ['adventure'], tags: ['activities', 'chill'] },
  'Relax & Lifestyle': { categories: ['culture', 'food'], tags: ['chill'] },
};

// Words an event title/description/tags would actually use for an interest
// that isn't literally spelled the same way. Anything not listed falls back
// to the interest's own lowercase text, which already matches the many
// interests that are plain event vocabulary ("Yoga", "Jazz", "Karaoke").
export const INTEREST_SYNONYMS: Record<string, string[]> = {
  'clubbing': ['club', 'clubbing', 'nightclub', 'party', 'parties'],
  'house music': ['house'],
  'live djs': ['dj', 'djs'],
  'pub crawls': ['pub crawl', 'bar crawl'],
  'after parties': ['after party', 'afterparty', 'after-party'],
  'festivals': ['festival'],
  'dance': ['dance', 'dancing', 'salsa', 'bachata'],
  'hip hop': ['hip hop', 'hip-hop', 'hiphop', 'rap'],
  'rap': ['rap', 'hip hop', 'hip-hop'],
  'live concerts': ['concert', 'live music', 'gig'],
  'edm': ['edm', 'electronic'],
  'cafes': ['cafe', 'café', 'coffee'],
  'coffee': ['coffee', 'cafe', 'café'],
  'bars': ['bar', 'bars'],
  'cocktails': ['cocktail'],
  'wine tasting': ['wine'],
  'beer': ['beer', 'brewery'],
  'cooking': ['cooking', 'cook', 'culinary'],
  'street food': ['street food', 'food truck', 'market'],
  'fine dining': ['dinner', 'fine dining', 'tasting menu'],
  'vegan food': ['vegan', 'plant-based'],
  'meeting people': ['meet', 'meetup', 'mixer', 'social'],
  'languages': ['language', 'tandem', 'lingua'],
  'cultural exchange': ['cultural', 'exchange', 'international'],
  'networking': ['networking', 'meetup'],
  'museums': ['museum', 'exhibition', 'gallery'],
  'photography': ['photo', 'photography'],
  'photography walks': ['photo walk', 'photography'],
  'painting': ['painting', 'art'],
  'gaming': ['gaming', 'game', 'games', 'esports'],
  'pc gaming': ['gaming', 'lan'],
  'console gaming': ['gaming', 'console'],
  'startups': ['startup', 'founder'],
  'coding': ['coding', 'hackathon', 'developer'],
  'ai': ['ai', 'machine learning'],
  'hiking': ['hike', 'hiking', 'trail'],
  'running': ['run', 'running', 'jog'],
  'cycling': ['cycling', 'bike'],
  'martial arts': ['martial arts', 'boxing', 'judo', 'karate'],
  'gym': ['gym', 'workout', 'fitness'],
  'meditation': ['meditation', 'mindfulness'],
  'nature': ['nature', 'park', 'outdoor'],
  'picnics': ['picnic'],
  'climbing': ['climbing', 'bouldering'],
  'city exploration': ['city tour', 'walking tour', 'explore'],
  'volunteering': ['volunteer', 'charity'],
};
